import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { CreateWorkOrderForm } from './CreateWorkOrderForm';
import { workOrderService } from '../../services/workOrderService';
import { inventoryService } from '../../services/inventoryService';
import type { CreateWorkOrderData, WorkOrder, WorkOrderStatus } from '../../types/workOrder';
import { useLanguage } from '../../features/language/useLanguage';
import { usePermissions } from '../../hooks/usePermissions';
import { SUBSCRIPTION_QUERY_KEY } from '../../hooks/useSubscription';
import { RoastQuotaExceededModal } from '../../components/subscription/RoastQuotaExceededModal';
import type { SubscriptionTier } from '../../services/subscriptionService';
import { useOnboardingStore } from '../../store/onboardingStore';
import { useToast } from '../../components/common/Toast';
import { isQuotaExceededError, type QuotaExceededPayload } from './quotaError';
import {
  CreateWorkOrderModeToggle,
  type CreateWorkOrderMode,
} from './components/CreateWorkOrderModeToggle';
import { BulkWorkOrderPanel } from './bulk/BulkWorkOrderPanel';

export const CreateWorkOrderPage: React.FC = () => {
  const { t } = useLanguage();
  const { can } = usePermissions();
  const canWrite = can('work_orders.roasting', 'write');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [currentOrder, setCurrentOrder] = React.useState<WorkOrder | null>(null);
  const [quotaError, setQuotaError] = React.useState<QuotaExceededPayload | null>(null);
  const [mode, setMode] = React.useState<CreateWorkOrderMode>('single');
  // Flipped by the bulk panel the moment its first draft exists.
  const [bulkStarted, setBulkStarted] = React.useState(false);
  const handleBulkStarted = React.useCallback(() => setBulkStarted(true), []);
  const handleBulkDone = React.useCallback(
    (message: string) => {
      showToast(message, 'success');
      navigate('/work-orders');
    },
    [navigate, showToast],
  );
  const onboardingState = useOnboardingStore((s) => s.state);
  const onboardingStep3 = onboardingState?.status === 'in_progress' && onboardingState?.currentStep === 3;

  // Tell the onboarding overlay a practice roast finished so it advances to
  // step 4. The guided flow always saves as draft first (which sets
  // currentOrder), so completion runs through the edit-mode branch below — both
  // completion paths must fire this, not just the first-create one.
  const notifyOnboardingComplete = (woId: string) => {
    if (onboardingStep3) {
      window.dispatchEvent(new CustomEvent('qrema:work-order-completed', { detail: { woId } }));
    }
  };

  const handleSubmit = async (data: CreateWorkOrderData, action: 'draft' | 'schedule' | 'complete') => {
    if (!canWrite) return;
    try {
      if (!currentOrder) {
        // Tag this WO as an onboarding sample when on step 3 so it can be
        // cleaned up at the end of the tour.
        const submitData: CreateWorkOrderData = onboardingStep3
          ? { ...data, onboardingSample: true }
          : data;
        const created = await workOrderService.create(submitData);
        let nextOrder: WorkOrder = created;
        if (created?.id) {
          const targetStatus: WorkOrderStatus = action === 'schedule' ? 'scheduled' : 'draft';
          try {
            if (action === 'complete') {
              await workOrderService.updateStatus(created.id, 'scheduled');
              nextOrder = await workOrderService.updateStatus(created.id, 'completed', data.outputExpiryDate);
              // Ensure outputWeight is available for inventory creation
              const orderForInventory = { ...nextOrder, outputWeight: data.outputWeight ?? created.outputWeight };
              await inventoryService.createFromWorkOrder(created.id, orderForInventory, data.outputExpiryDate);
              queryClient.invalidateQueries({ queryKey: ['work-orders'] });
              queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
              queryClient.invalidateQueries({ queryKey: ['inventory-items-all'] });
              notifyOnboardingComplete(created.id);
              navigate('/work-orders');
              return;
            } else {
              nextOrder = await workOrderService.updateStatus(created.id, targetStatus);
            }
          } catch (statusErr) {
            console.error('Failed to update work order status:', statusErr);
          }
        }
        setCurrentOrder(nextOrder);
      } else {
        const updated = await workOrderService.update(currentOrder.id, data);
        let nextOrder = updated;
        if (currentOrder.status === 'draft' && (action === 'schedule' || action === 'complete')) {
          try {
            nextOrder = await workOrderService.updateStatus(currentOrder.id, 'scheduled');
            if (action === 'complete') {
              nextOrder = await workOrderService.updateStatus(currentOrder.id, 'completed', data.outputExpiryDate);
              // Ensure outputWeight is available for inventory creation
              const orderForInventory = { ...nextOrder, outputWeight: data.outputWeight ?? updated.outputWeight };
              await inventoryService.createFromWorkOrder(currentOrder.id, orderForInventory, data.outputExpiryDate);
            }
          } catch (statusErr) {
            console.error('Failed to move work order to scheduled/completed:', statusErr);
          }
          queryClient.invalidateQueries({ queryKey: ['work-orders'] });
          queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
          if (action === 'complete') {
            queryClient.invalidateQueries({ queryKey: ['inventory-items-all'] });
            notifyOnboardingComplete(currentOrder.id);
          }
          navigate('/work-orders');
          return;
        } else if (action === 'complete') {
           // Handle case where status was already scheduled (or other non-draft status if applicable)
           try {
             nextOrder = await workOrderService.updateStatus(currentOrder.id, 'completed', data.outputExpiryDate);
             // Ensure outputWeight is available for inventory creation
             const orderForInventory = { ...nextOrder, outputWeight: data.outputWeight ?? updated.outputWeight };
             await inventoryService.createFromWorkOrder(currentOrder.id, orderForInventory, data.outputExpiryDate);
             queryClient.invalidateQueries({ queryKey: ['inventory-items-all'] });
             queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
             notifyOnboardingComplete(currentOrder.id);
             navigate('/work-orders');
             return;
           } catch (err) {
             console.error('Failed to complete work order:', err);
           }
        }
        setCurrentOrder(nextOrder);
      }
      queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
    } catch (err) {
      // PRD 76: per-cycle roast quota exceeded — show the upgrade modal and
      // refresh subscription state so the usage card updates.
      if (isQuotaExceededError(err)) {
        setQuotaError(err.response.data);
        queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
        throw err;
      }
      console.error('Failed to submit work order:', err);
      alert(t('workOrders.page.errors.updateFailed'));
      throw err;
    }
  };

  return (
    <div className="space-y-6" data-test-id="create-work-order-page">
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => navigate('/work-orders')}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          data-test-id="wo-create-back-button"
        >
          <ArrowLeft className="h-6 w-6 text-gray-600" />
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">{t('dashboard.page.modalCreateWorkOrder')}</h1>
      </div>

      {/* The toggle is a create-time choice only: once a bulk draft exists,
          switching modes would strand real work orders. It is also hidden
          during onboarding step 3, whose gate only accepts a completed work
          order — an outcome bulk mode cannot produce. */}
      {canWrite && !currentOrder && !bulkStarted && !onboardingStep3 && (
        <CreateWorkOrderModeToggle mode={mode} onChange={setMode} />
      )}

      <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
        {mode === 'bulk' && !currentOrder ? (
          <BulkWorkOrderPanel
            readOnly={!canWrite}
            onCancel={() => navigate('/work-orders')}
            onQuotaExceeded={setQuotaError}
            onStarted={handleBulkStarted}
            onDone={handleBulkDone}
          />
        ) : (
          <CreateWorkOrderForm
            onSubmit={handleSubmit}
            initialData={currentOrder || undefined}
            onCancel={() => navigate('/work-orders')}
            readOnly={!canWrite}
          />
        )}
      </div>

      {quotaError && (
        <RoastQuotaExceededModal
          isOpen
          onClose={() => setQuotaError(null)}
          tier={quotaError.tier as SubscriptionTier}
          limitKg={quotaError.limit_kg}
          usedKg={quotaError.used_kg}
          requestedKg={quotaError.requested_kg}
          cycleEnd={new Date(quotaError.cycle_end)}
        />
      )}
    </div>
  );
};
